import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaFileVideo,
    FaImage,
    FaClock,
    FaToggleOn,
    FaToggleOff,
    FaBookOpen,
    FaListUl,
    FaCalendarAlt,
    FaExclamationCircle,
    FaEye,
    FaGlobe,
    FaLayerGroup,
    FaUserGraduate,
    FaDollarSign
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import './TutorDashboardOverview.css';
import '../../SuperAdmin/views/UserModals.css'; // Shared premium modal styling
import { FaTimes, FaChevronRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { COURSE_CATEGORIES } from '../../../constants';

// Initial Form State matching backend schema
const initialFormState = {
    title: '',
    description: '',
    category: COURSE_CATEGORIES[0],
    customCategory: '',
    difficultyLevel: 'Beginner',
    prerequisites: '', // Handled as comma-separated string locally
    courseMode: 'Online',
    pricingType: 'Paid',
    price: '',
    language: 'English',
    customLanguage: '',
    modules: [{ moduleTitle: '', moduleDescription: '' }],
    batches: [{ batchName: '', startDate: '', endDate: '', maxStudents: '' }],
    thumbnailUrl: null,
    promotionalVideoUrl: null
};

const TutorCourses = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCourse, setEditingCourse] = useState(null);
    const [viewingCourse, setViewingCourse] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialFormState);
    const navigate = useNavigate();

    const fetchCourses = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/tutor/courses', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCourses(res.data);
            setLoading(false);
        } catch (err) {
            showError('Fetch Failed', 'Could not load your courses. Please try again later.');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    useEffect(() => {
        if (isModalOpen || isViewModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isModalOpen, isViewModalOpen]);

    const openCreateModal = () => {
        console.log("Opening Create Modal...");
        setEditingCourse(null);
        setFormData(initialFormState);
        setIsModalOpen(true);
    };

    const openEditModal = (course) => {
        setEditingCourse(course);
        const predefinedCategories = COURSE_CATEGORIES;
        const predefinedLanguages = ['English', 'Spanish', 'French', 'Hindi'];

        const isCustomCategory = !predefinedCategories.includes(course.category);
        const isCustomLanguage = !predefinedLanguages.includes(course.language);

        setFormData({
            title: course.title,
            description: course.description,
            category: isCustomCategory ? 'Other_Custom' : course.category,
            customCategory: isCustomCategory ? course.category : '',
            difficultyLevel: course.difficultyLevel,
            prerequisites: course.prerequisites.join(', '),
            courseMode: course.courseMode,
            pricingType: course.pricingType,
            price: course.price,
            language: isCustomLanguage ? 'Other_Custom' : course.language,
            customLanguage: isCustomLanguage ? course.language : '',
            modules: course.modules?.length > 0 ? course.modules : [{ moduleTitle: '', moduleDescription: '' }],
            batches: course.batches.length > 0 ? course.batches.map(b => ({
                ...b,
                startDate: new Date(b.startDate).toISOString().split('T')[0],
                endDate: new Date(b.endDate).toISOString().split('T')[0]
            })) : [{ batchName: '', startDate: '', endDate: '', maxStudents: '' }],
            thumbnailUrl: null, // Don't reload binary
            promotionalVideoUrl: null
        });
        setIsModalOpen(true);
    };

    const openViewModal = (course) => {
        setViewingCourse(course);
        setIsViewModalOpen(true);
    };

    // Form Event Handlers
    const handleBasicChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.files[0] });
    };

    // Arrays Event Handlers
    const handleModuleChange = (index, field, value) => {
        const newModules = [...formData.modules];
        newModules[index][field] = value;
        setFormData({ ...formData, modules: newModules });
    };
    const addModule = () => setFormData({ ...formData, modules: [...formData.modules, { moduleTitle: '', moduleDescription: '' }] });
    const removeModule = (index) => setFormData({ ...formData, modules: formData.modules.filter((_, i) => i !== index) });

    const handleBatchChange = (index, field, value) => {
        const newBatches = [...formData.batches];
        newBatches[index][field] = value;
        setFormData({ ...formData, batches: newBatches });
    };
    const addBatch = () => setFormData({ ...formData, batches: [...formData.batches, { batchName: '', startDate: '', endDate: '', maxStudents: '' }] });
    const removeBatch = (index) => setFormData({ ...formData, batches: formData.batches.filter((_, i) => i !== index) });

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoading('Saving Course Structure...');

        const token = localStorage.getItem('token');
        const submitData = new FormData();

        // 1. Basic Fields
        submitData.append('title', formData.title);
        submitData.append('description', formData.description);
        submitData.append('category', formData.category === 'Other_Custom' ? formData.customCategory : formData.category);
        submitData.append('difficultyLevel', formData.difficultyLevel);
        submitData.append('courseMode', formData.courseMode);
        submitData.append('pricingType', formData.pricingType);
        submitData.append('price', formData.pricingType === 'Paid' ? formData.price : 0);
        submitData.append('language', formData.language === 'Other_Custom' ? formData.customLanguage : formData.language);

        // Convert comma string to JSON array
        const prereqs = formData.prerequisites.split(',').map(s => s.trim()).filter(s => s !== '');
        submitData.append('prerequisites', JSON.stringify(prereqs));

        // 2. Arrays
        submitData.append('modules', JSON.stringify(formData.modules));
        submitData.append('batches', JSON.stringify(formData.batches));

        // 3. Files
        if (formData.thumbnailUrl) submitData.append('thumbnailUrl', formData.thumbnailUrl);
        if (formData.promotionalVideoUrl) submitData.append('promotionalVideoUrl', formData.promotionalVideoUrl);

        try {
            if (editingCourse) {
                await axios.put(`/api/tutor/courses/${editingCourse._id}`, submitData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showSuccess('Course Updated', 'Course changes submitted successfully and returning to Pending Approval.');
            } else {
                await axios.post('/api/tutor/courses', submitData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showSuccess('Course Created', 'Your course has been pushed to the Super Admin for pending approval.');
            }

            closeLoading();
            setIsModalOpen(false);
            fetchCourses(); // Refresh courses list
        } catch (err) {
            closeLoading();
            showError('Submission Failed', err.response?.data?.message || 'Please check your inputs and try again.');
        }
    };

    const courseColumns = [
        { header: 'Course Title', key: 'title', width: '30%' },
        {
            header: 'Category',
            key: 'category',
            width: '20%',
            render: (row) => <span style={{ color: 'var(--text-muted)' }}>{row.category}</span>
        },
        {
            header: 'Syllabus',
            key: 'modules',
            width: '10%',
            render: (row) => `${row.modules?.length || 0} Modules`
        },
        {
            header: 'Price',
            key: 'price',
            width: '10%',
            render: (row) => row.pricingType === 'Free' ? 'Free' : `$${row.price}`
        },
        {
            header: 'Status',
            key: 'status',
            width: '15%',
            render: (row) => (
                <span className={`status-badge ${(row.status || 'Pending').toLowerCase()}`}>
                    {row.status || 'Pending'}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            width: '15%',
            render: (row) => (
                <div className="action-buttons">
                    <button className="action-btn view" title="View Course Details" onClick={() => openViewModal(row)}>
                        <FaEye />
                    </button>
                    <button className="action-btn edit" title="Edit Course Structure" onClick={() => openEditModal(row)}>
                        <FaEdit />
                    </button>
                    {row.status === 'Active' && (
                        <button className="action-btn video" title="Manage Course Content" onClick={() => navigate('/admin/tutor-lms')}>
                            <FaFileVideo />
                        </button>
                    )}
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    console.log("TutorCourses Render - isModalOpen:", isModalOpen, "formData:", formData);

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 className="page-title">My Courses</h1>
                    <p className="page-subtitle">Outline your educational syllabus structure and track admin approvals.</p>
                </div>
                <button className="primary-btn pulse-on-hover" onClick={openCreateModal}>
                    <FaPlus style={{ marginRight: '8px' }} /> Create New Course
                </button>
            </div>

            {/* Rejection Banners */}
            {courses.filter(c => c.status === 'Rejected').length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                    {courses.filter(c => c.status === 'Rejected').map(course => (
                        <div key={course._id} style={{
                            background: '#FEF2F2', border: '1px solid #DC2626', borderRadius: '12px',
                            padding: '20px', marginBottom: '16px', display: 'flex', alignItems: 'start', gap: '16px'
                        }}>
                            <FaExclamationCircle style={{ color: '#DC2626', fontSize: '24px', marginTop: '4px' }} />
                            <div>
                                <h3 style={{ color: '#991B1B', margin: '0 0 8px 0' }}>Action Required: "{course.title}" was Returned</h3>
                                <p style={{ color: '#B91C1C', margin: 0, fontSize: '14px' }}><strong>Admin Feedback:</strong> {course.adminFeedback}</p>
                                <button
                                    onClick={() => openEditModal(course)}
                                    style={{ marginTop: '12px', background: '#DC2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}
                                >
                                    Modify and Resubmit
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <DataTable
                title="Your Architected Courses"
                columns={courseColumns}
                data={courses}
                filterOptions={[
                    { label: 'Pending Approval', value: 'Pending' },
                    { label: 'Active (Live)', value: 'Active' },
                    { label: 'Returned', value: 'Rejected' },
                ]}
            />

            {/* Course Builder Modal (Mirroring UserManagement Modal Structure) */}
            {createPortal(
                <div className={`modal-overlay ${isModalOpen ? 'open' : ''}`} onClick={() => setIsModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                        <div className="modal-header">
                            <h2><FaBookOpen /> {editingCourse ? 'Resolve & Resubmit Course' : 'Course Syllabus Builder'}</h2>
                            <button className="modal-close-btn" type="button" onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            {editingCourse && editingCourse.status === 'Rejected' && (
                                <div style={{ background: '#FEF2F2', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #DC2626', marginBottom: '24px' }}>
                                    <p style={{ margin: 0, color: '#991B1B' }}><strong>Admin Feedback to Fix:</strong> {editingCourse.adminFeedback}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="modal-form">
                                {/* Section 1: Basic Info */}
                                <div className="form-group full-width">
                                    <h3 style={{ color: 'var(--primary-blue)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', margin: 0 }}>1. Basic Course Identity</h3>
                                </div>

                                <div className="form-group full-width">
                                    <label className="premium-label">Course Title</label>
                                    <input type="text" name="title" value={formData.title} onChange={handleBasicChange} className="glass-input" required placeholder="e.g. Masterclass in React" />
                                </div>

                                <div className="form-group full-width">
                                    <label className="premium-label">Course Description & Objectives</label>
                                    <textarea name="description" value={formData.description} onChange={handleBasicChange} className="glass-input resize-none" rows="3" required placeholder="What will students learn?" />
                                </div>

                                <div className="form-group">
                                    <label className="premium-label">Category</label>
                                    <select name="category" value={formData.category} onChange={handleBasicChange} className="glass-input">
                                        {COURSE_CATEGORIES.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                        <option value="Other_Custom">Other (Type below)</option>
                                    </select>
                                    {formData.category === 'Other_Custom' && (
                                        <input type="text" name="customCategory" value={formData.customCategory} onChange={handleBasicChange} className="glass-input" placeholder="Type custom category..." style={{ marginTop: '8px' }} required />
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="premium-label">Difficulty Level</label>
                                    <select name="difficultyLevel" value={formData.difficultyLevel} onChange={handleBasicChange} className="glass-input">
                                        <option value="Beginner">Beginner</option>
                                        <option value="Intermediate">Intermediate</option>
                                        <option value="Advanced">Advanced</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="premium-label">Language</label>
                                    <select name="language" value={formData.language} onChange={handleBasicChange} className="glass-input">
                                        <option value="English">English</option>
                                        <option value="Hindi">Hindi</option>
                                        <option value="Spanish">Spanish</option>
                                        <option value="French">French</option>
                                        <option value="Other_Custom">Other (Type below)</option>
                                    </select>
                                    {formData.language === 'Other_Custom' && (
                                        <input type="text" name="customLanguage" value={formData.customLanguage} onChange={handleBasicChange} className="glass-input" placeholder="Type custom language..." style={{ marginTop: '8px' }} required />
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="premium-label">Prerequisites (Comma separated)</label>
                                    <input type="text" name="prerequisites" value={formData.prerequisites} onChange={handleBasicChange} className="glass-input" placeholder="e.g. Basic HTML" />
                                </div>

                                {/* Section 2: Logistics */}
                                <div className="form-group full-width">
                                    <h3 style={{ color: 'var(--primary-blue)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', margin: '16px 0 0 0' }}>2. Logistics & Setup</h3>
                                </div>

                                <div className="form-group">
                                    <label className="premium-label">Course Mode</label>
                                    <select name="courseMode" value={formData.courseMode} onChange={handleBasicChange} className="glass-input">
                                        <option value="Online">100% Online</option>
                                        <option value="Offline">Offline / In-Person</option>
                                        <option value="Hybrid">Hybrid Approach</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="premium-label">Pricing Strategy</label>
                                    <select name="pricingType" value={formData.pricingType} onChange={handleBasicChange} className="glass-input">
                                        <option value="Paid">Premium (Paid)</option>
                                        <option value="Free">Free</option>
                                    </select>
                                </div>

                                {formData.pricingType === 'Paid' && (
                                    <div className="form-group full-width">
                                        <label className="premium-label">Price ($ USD)</label>
                                        <input type="number" min="1" name="price" value={formData.price} onChange={handleBasicChange} className="glass-input" required />
                                    </div>
                                )}

                                {/* Section 3: Media */}
                                <div className="form-group full-width">
                                    <h3 style={{ color: 'var(--primary-blue)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', margin: '16px 0 0 0' }}>3. Branding Content</h3>
                                </div>
                                <div className="form-group" style={{ padding: '15px', border: '1px dashed var(--border-color)', borderRadius: '8px', background: 'var(--bg-main)' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: 'var(--primary-blue)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><FaImage /> Thumbnail Image</h4>
                                    <input type="file" name="thumbnailUrl" accept="image/*" onChange={handleFileChange} className="glass-input" style={{ border: 'none', background: 'white', padding: '8px' }} />
                                </div>
                                <div className="form-group" style={{ padding: '15px', border: '1px dashed var(--border-color)', borderRadius: '8px', background: 'var(--bg-main)' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: 'var(--primary-blue)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><FaFileVideo /> Promo Video (Optional)</h4>
                                    <input type="file" name="promotionalVideoUrl" accept="video/mp4,video/x-m4v,video/*" onChange={handleFileChange} className="glass-input" style={{ border: 'none', background: 'white', padding: '8px' }} />
                                </div>

                                {/* Section 4: Syllabus/Modules */}
                                <div className="form-group full-width">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 0 0', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
                                        <h3 style={{ color: 'var(--primary-blue)', margin: 0 }}>4. Syllabus Blueprint Layers (Modules)</h3>
                                        <button type="button" onClick={addModule} className="action-btn-gold" style={{ padding: '6px 16px', fontSize: '12px' }}>+ Add Module</button>
                                    </div>
                                </div>

                                {formData.modules.map((module, index) => (
                                    <div key={index} className="form-group full-width" style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-main)', position: 'relative' }}>
                                        {formData.modules.length > 1 && (
                                            <button type="button" onClick={() => removeModule(index)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', color: '#EF4444', border: 'none', cursor: 'pointer' }}><FaTrash /></button>
                                        )}
                                        <div className="form-group" style={{ marginBottom: '12px' }}>
                                            <label className="premium-label">Module #{index + 1} Name</label>
                                            <input type="text" value={module.moduleTitle} onChange={(e) => handleModuleChange(index, 'moduleTitle', e.target.value)} className="glass-input" required placeholder="e.g. Introduction & Fundamentals" style={{ width: '90%' }} />
                                        </div>
                                        <div className="form-group">
                                            <label className="premium-label">Module Summary</label>
                                            <textarea value={module.moduleDescription} onChange={(e) => handleModuleChange(index, 'moduleDescription', e.target.value)} className="glass-input resize-none" rows="2" placeholder="Brief outline of this module..." />
                                        </div>
                                    </div>
                                ))}

                                {/* Section 5: Batches */}
                                <div className="form-group full-width">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 0 0', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
                                        <h3 style={{ color: 'var(--primary-blue)', margin: 0 }}>5. Cohort/Batch Schedules</h3>
                                        <button type="button" onClick={addBatch} className="action-btn-gold" style={{ padding: '6px 16px', fontSize: '12px' }}>+ Add Batch</button>
                                    </div>
                                </div>

                                {formData.batches.map((batch, index) => (
                                    <div key={index} className="form-group full-width" style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '8px', position: 'relative' }}>
                                        {formData.batches.length > 1 && (
                                            <button type="button" onClick={() => removeBatch(index)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', color: '#EF4444', border: 'none', cursor: 'pointer' }}><FaTrash /></button>
                                        )}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                            <div className="form-group">
                                                <label className="premium-label">Batch Name</label>
                                                <input type="text" value={batch.batchName} onChange={(e) => handleBatchChange(index, 'batchName', e.target.value)} className="glass-input" required placeholder="e.g. Summer Weekend Batch" />
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Capacity limit</label>
                                                <input type="number" min="1" value={batch.maxStudents} onChange={(e) => handleBatchChange(index, 'maxStudents', e.target.value)} className="glass-input" required placeholder="Max Students" />
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Start Date</label>
                                                <input type="date" value={batch.startDate} onChange={(e) => handleBatchChange(index, 'startDate', e.target.value)} className="glass-input" required />
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">End Date</label>
                                                <input type="date" value={batch.endDate} onChange={(e) => handleBatchChange(index, 'endDate', e.target.value)} className="glass-input" required />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="action-btn-gold pulse-on-hover">{editingCourse ? 'Resolve & Resubmit' : 'Submit Course'} <FaChevronRight style={{ marginLeft: '10px' }} /></button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* HIGHLY STYLED COURSE VIEW MODAL */}
            {isViewModalOpen && viewingCourse && createPortal(
                <div className={`modal-overlay ${isViewModalOpen ? 'open' : ''}`} onClick={() => setIsViewModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', height: '85vh' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #002C5F, #00458E)', color: 'white' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '10px', borderRadius: '12px' }}>
                                    <FaBookOpen size={24} />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}>{viewingCourse.title}</h2>
                                    <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>Course Blueprint & Syllabus</span>
                                </div>
                            </div>
                            <button className="modal-close-btn" onClick={() => setIsViewModalOpen(false)}><FaTimes /></button>
                        </div>

                        <div className="modal-body" style={{ background: '#f8fafc', padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <div className="view-modal-layout" style={{ display: 'flex', flexWrap: 'nowrap', height: '100%', overflow: 'hidden' }}>
                                {/* Left Sidebar: Media Content - FIXED */}
                                <div style={{ flex: '0 0 400px', padding: '24px', background: 'white', borderRight: '1px solid #e2e8f0', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <h4 className="premium-label" style={{ marginBottom: '15px', color: '#64748b' }}>COURSE PREVIEW MEDIA</h4>

                                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }} className="custom-scrollbar">
                                        <div className="thumbnail-preview" style={{ marginBottom: '25px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', background: '#000', position: 'relative' }}>
                                            <label className="premium-label" style={{ position: 'absolute', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 12px', borderRadius: '0 0 12px 0', fontSize: '11px' }}>REPRESENTATIVE IMAGE</label>
                                            <img
                                                src={viewingCourse.thumbnailUrl ? (viewingCourse.thumbnailUrl.startsWith('http') ? viewingCourse.thumbnailUrl : `${axios.defaults.baseURL || ''}${viewingCourse.thumbnailUrl}`) : 'https://via.placeholder.com/800x450?text=No+Thumbnail'}
                                                alt="Thumbnail"
                                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                            />
                                        </div>

                                        {viewingCourse.promotionalVideoUrl && (
                                            <div className="video-preview" style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', background: '#000', position: 'relative' }}>
                                                <label className="premium-label" style={{ position: 'absolute', zIndex: 10, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 12px', borderRadius: '0 0 12px 0', fontSize: '11px' }}>PROMOTIONAL VIDEO</label>
                                                <video controls style={{ width: '100%', display: 'block' }}>
                                                    <source src={viewingCourse.promotionalVideoUrl.startsWith('http') ? viewingCourse.promotionalVideoUrl : `${axios.defaults.baseURL || ''}${viewingCourse.promotionalVideoUrl}`} type="video/mp4" />
                                                    Your browser does not support the video tag.
                                                </video>
                                            </div>
                                        )}

                                        {!viewingCourse.promotionalVideoUrl && (
                                            <div style={{ padding: '30px', textAlign: 'center', background: '#f1f5f9', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                                                <FaFileVideo size={30} color="#94a3b8" />
                                                <p style={{ margin: '10px 0 0 0', color: '#64748b', fontSize: '13px' }}>No promotional video uploaded for this course.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Content: Course Details - SCROLLABLE */}
                                <div style={{ flex: '1', padding: '30px', overflowY: 'auto' }}>
                                    {viewingCourse.status === 'Rejected' && viewingCourse.adminFeedback && (
                                        <div style={{ padding: '16px', background: '#FEF2F2', border: '1px solid #F87171', borderRadius: '12px', marginBottom: '24px', display: 'flex', gap: '12px' }}>
                                            <FaExclamationCircle color="#DC2626" size={24} style={{ flexShrink: 0, marginTop: '2px' }} />
                                            <div>
                                                <h4 style={{ margin: '0 0 4px 0', color: '#991B1B', fontSize: '14px' }}>Return for Edit Feedback</h4>
                                                <p style={{ margin: 0, color: '#7F1D1D', fontSize: '14px', lineHeight: 1.5 }}>
                                                    {viewingCourse.adminFeedback}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaGlobe /> CATEGORY</div>
                                            <div style={{ color: '#0f172a', fontWeight: 600, marginTop: '4px' }}>{viewingCourse.category}</div>
                                        </div>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaLayerGroup /> DIFFICULTY</div>
                                            <div style={{ color: '#0f172a', fontWeight: 600, marginTop: '4px' }}>{viewingCourse.difficultyLevel}</div>
                                        </div>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaUserGraduate /> LANGUAGE</div>
                                            <div style={{ color: '#0f172a', fontWeight: 600, marginTop: '4px' }}>{viewingCourse.language}</div>
                                        </div>
                                        <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}><FaDollarSign /> PRICING</div>
                                            {viewingCourse.pricingType === 'Free' ? 'FREE' : `$ ${viewingCourse.price}`}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '30px' }}>
                                        <h4 className="premium-label" style={{ borderBottom: '2px solid #002C5F', paddingBottom: '6px', display: 'inline-block' }}>ABOUT THIS COURSE</h4>
                                        <p style={{ color: '#334155', lineHeight: 1.6, marginTop: '12px', fontSize: '15px' }}>{viewingCourse.description}</p>
                                    </div>

                                    <div style={{ marginBottom: '30px' }}>
                                        <h4 className="premium-label">PREREQUISITES</h4>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                            {viewingCourse.prerequisites && viewingCourse.prerequisites.length > 0 ? viewingCourse.prerequisites.map((p, i) => (
                                                <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '6px 14px', borderRadius: '30px', fontSize: '13px', fontWeight: 500 }}>{p}</span>
                                            )) : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No specific prerequisites listed.</span>}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '30px' }}>
                                        <h4 className="premium-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>COURSE CURRICULUM</span>
                                            <span style={{ color: '#64748b', fontSize: '12px' }}>{viewingCourse.modules?.length || 0} Modules Total</span>
                                        </h4>
                                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {viewingCourse.modules?.map((m, i) => (
                                                <div key={i} style={{ padding: '16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                    <div style={{ fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <span style={{ background: '#002C5F', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>{i + 1}</span>
                                                        {m.moduleTitle}
                                                    </div>
                                                    <div style={{ color: '#64748b', fontSize: '13px', marginTop: '6px', paddingLeft: '34px' }}>{m.moduleDescription}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 className="premium-label">AVAILABLE BATCHES</h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginTop: '12px' }}>
                                            {viewingCourse.batches?.map((b, i) => (
                                                <div key={i} style={{ padding: '16px', border: '1px solid #e2e8f0', background: 'white', borderRadius: '12px', borderLeft: '4px solid #D4AF37' }}>
                                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{b.batchName}</div>
                                                    <div style={{ display: 'flex', color: '#64748b', fontSize: '12px', marginTop: '8px', gap: '12px' }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FaCalendarAlt size={10} /> {new Date(b.startDate).toLocaleDateString()}</span>
                                                        <span>-</span>
                                                        <span>{new Date(b.endDate).toLocaleDateString()}</span>
                                                    </div>
                                                    <div style={{ color: '#002C5F', fontSize: '12px', fontWeight: 600, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                        <FaPlus size={8} /> Max Intake: {b.maxStudents} Students
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TutorCourses;
